/**
 * Enhanced Questionnaire Component
 * Handles all 308 questions with dynamic branching, repeatable fields, and progress tracking
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getVisibleQuestions, getNextQuestion, getPreviousQuestion } from '../data/enhanced-questionnaire-loader';
import { QuestionnaireState, FormData, Section } from '../types/questionnaire';
import EnhancedQuestion from './EnhancedQuestion';
import ProgressBar from './ProgressBar';
import CountryYearSelection, { Country, TaxYear } from './CountryYearSelection';
import { Save, CheckCircle, Loader, Trash2, X } from 'lucide-react';
import { useLoadQuestionnaire } from '../features/questionnaire/hooks/useLoadQuestionnaire';
import { useSaveQuestionnaire } from '../features/expat/hooks/useSaveQuestionnaire';
import { convertJSONToSections, getInlineQuestionnaireData } from '../data/enhanced-questionnaire-loader';

interface EnhancedQuestionnaireProps {
  onComplete: (data: FormData) => void;
  onSave: (data: FormData) => void;
  initialData?: FormData;
}

const EnhancedQuestionnaire: React.FC<EnhancedQuestionnaireProps> = ({
  onComplete,
  onSave,
  initialData = {}
}) => {
  // Country and year selection state
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [selectedYear, setSelectedYear] = useState<TaxYear | null>(null);
  const [showSelection, setShowSelection] = useState(true);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  // Use React Query to load questionnaire data (only when country and year are selected)
  const { data: questionnaireData, isLoading: isLoadingQuestionnaire, error: questionnaireError } = useLoadQuestionnaire(
    selectedCountry,
    selectedYear,
    !showSelection && selectedCountry !== null && selectedYear !== null
  );
  const { mutateAsync: saveQuestionnaire, isPending: isSaving } = useSaveQuestionnaire();

  const [sections, setSections] = useState<Section[]>([]);
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState<QuestionnaireState>({
    currentSection: 0,
    currentQuestion: 0,
    responses: new Map(),
    completedSections: new Set(),
    isComplete: false,
    progress: 0
  });

  const [formData, setFormData] = useState<FormData>(initialData);
  const [showValidation, setShowValidation] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Track navigation state to prevent unnecessary re-runs
  // Only tracks which questions have answers (keys), not the values, to avoid triggering on typing
  const navigationCheckRef = useRef<{ lastFormDataHash: string; lastSectionsHash: string }>({
    lastFormDataHash: '',
    lastSectionsHash: ''
  });

  // Handle country and year selection
  const handleCountryYearSelect = useCallback((country: Country, year: TaxYear) => {
    setSelectedCountry(country);
    setSelectedYear(year);
    setShowSelection(false);
    // Store in formData for later use
    setFormData(prev => ({
      ...prev,
      country,
      tax_year: year
    }));
  }, []);

  // Load questionnaire data and convert to sections
  useEffect(() => {
    // Don't load if still showing selection screen
    if (showSelection) {
      return;
    }

    if (isLoadingQuestionnaire) {
      setLoading(true);
      return;
    }

    if (questionnaireError) {
      console.error('Error loading questionnaire:', questionnaireError);
      // Fallback to inline data
      const fallbackSections = getInlineQuestionnaireData();
      setSections(fallbackSections);
      setLoading(false);
      return;
    }

    if (questionnaireData && selectedCountry) {
      try {
        const loadedSections = convertJSONToSections(questionnaireData, selectedCountry);
        setSections(loadedSections);
      } catch (error) {
        console.error('Error converting questionnaire data:', error);
        const fallbackSections = getInlineQuestionnaireData();
        setSections(fallbackSections);
      }
    } else {
      // No data, use fallback
      const fallbackSections = getInlineQuestionnaireData();
      setSections(fallbackSections);
    }

    // Load saved progress from localStorage if available
    const savedProgress = localStorage.getItem('questionnaire_progress');
    if (savedProgress && !initialData) {
      try {
        const parsed = JSON.parse(savedProgress);
        // Restore country and year if saved
        if (parsed.formData?.country && parsed.formData?.tax_year) {
          setSelectedCountry(parsed.formData.country);
          setSelectedYear(parsed.formData.tax_year);
          setShowSelection(false);
        }
        setFormData(parsed.formData);
        setState({
          ...parsed.state,
          responses: new Map(parsed.state.responses || []),
          completedSections: new Set(parsed.state.completedSections || [])
        });
      } catch (error) {
        console.error('Error parsing saved progress:', error);
      }
    }

    setLoading(false);
  }, [isLoadingQuestionnaire, questionnaireData, questionnaireError, initialData, showSelection, selectedCountry]);

  // Get current section and visible questions
  const currentSection = useMemo(() => {
    if (sections.length === 0) return null;
    return sections[state.currentSection] || null;
  }, [sections, state.currentSection]);

  const visibleQuestions = useMemo(() => {
    if (!currentSection) return [];
    return getVisibleQuestions(currentSection, formData, state.currentSection);
  }, [currentSection, formData, state.currentSection]);

  const currentQuestion = useMemo(() => {
    if (visibleQuestions.length === 0) return null;
    
    // Get the current section's full questions array
    const currentSectionObj = sections[state.currentSection];
    if (!currentSectionObj) return null;
    
    // Try to find the question by the stored index first (for backward compatibility)
    const questionByIndex = currentSectionObj.questions[state.currentQuestion];
    
    // If the question at that index exists and is visible, use it
    if (questionByIndex && visibleQuestions.some(q => q.id === questionByIndex.id)) {
      // Find it in visibleQuestions to ensure we return the visible version
      return visibleQuestions.find(q => q.id === questionByIndex.id) || null;
    }
    
    // Otherwise, use a safe index into visibleQuestions
    const safeIndex = Math.min(state.currentQuestion, visibleQuestions.length - 1);
    return visibleQuestions[safeIndex] || null;
  }, [visibleQuestions, state.currentQuestion, state.currentSection, sections]);

  // Find the index of current question within visible questions (for display)
  const currentQuestionIndexInVisible = useMemo(() => {
    if (!currentQuestion || visibleQuestions.length === 0) return 0;
    const index = visibleQuestions.findIndex(q => q.id === currentQuestion.id);
    return index >= 0 ? index : 0;
  }, [currentQuestion, visibleQuestions]);

  // Auto-navigate to first visible question if current question is not visible
  // Only run when sections change (initial load) or when current question becomes truly invisible
  // IMPORTANT: This should NOT run on every formData change (like typing), only when visibility actually changes
  useEffect(() => {
    if (sections.length === 0) return;
    
    // Create a simple hash to detect if sections changed (initial load)
    const sectionsHash = sections.map(s => s.id).join(',');
    // Only hash the KEYS of formData, not the values, to avoid triggering on typing
    // We only care about which questions have been answered, not the exact values
    const formDataKeysHash = JSON.stringify(Object.keys(formData).sort());
    
    // Check if this is initial load (sections changed)
    const isInitialLoad = navigationCheckRef.current.lastSectionsHash !== sectionsHash;
    
    // Skip if nothing actually changed (not initial load and formData keys haven't changed)
    // This prevents re-running when user is just typing in a field
    if (!isInitialLoad && navigationCheckRef.current.lastFormDataHash === formDataKeysHash) {
      return;
    }
    
    // Store isInitialLoad before updating ref
    const wasInitialLoad = isInitialLoad;
    
    // Update ref (only store keys hash, not values)
    navigationCheckRef.current.lastFormDataHash = formDataKeysHash;
    navigationCheckRef.current.lastSectionsHash = sectionsHash;
    
    // Use functional setState to get current state without adding it to dependencies
    setState(prev => {
      // Use the stored isInitialLoad value
      const isInitialLoad = wasInitialLoad;
      
      // Get the current question ID to check if it's still visible
      const currentSectionObj = sections[prev.currentSection];
      if (!currentSectionObj) {
        // Invalid section, find first visible question
        for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex++) {
          const section = sections[sectionIndex];
          const visibleQs = getVisibleQuestions(section, formData, sectionIndex);
          if (visibleQs.length > 0) {
            return {
              ...prev,
              currentSection: sectionIndex,
              currentQuestion: 0
            };
          }
        }
        return prev;
      }
      
      // Get current question by ID (not by index, as index might have changed)
      const allQuestionsInSection = currentSectionObj.questions;
      const currentQuestionById = allQuestionsInSection[prev.currentQuestion];
      const currentQuestionId = currentQuestionById?.id;
      
      // Get visible questions for current section
      const currentSectionVisibleQs = getVisibleQuestions(
        currentSectionObj, 
        formData,
        prev.currentSection
      );
      
      // Check if current question (by ID) is still in the visible questions
      const isCurrentQuestionStillVisible = currentQuestionId && 
        currentSectionVisibleQs.some(q => q.id === currentQuestionId);
      
      // If current question is still visible, try to find its new index
      if (isCurrentQuestionStillVisible && currentSectionVisibleQs.length > 0) {
        // Find the question in the full section questions array to get its actual index
        const questionInFullArray = currentSectionObj.questions.findIndex(q => q.id === currentQuestionId);
        if (questionInFullArray !== -1 && questionInFullArray !== prev.currentQuestion) {
          // Question is visible but at a different index in the full array, update index
          return {
            ...prev,
            currentQuestion: questionInFullArray
          };
        }
        // Question is visible at the same index, no change needed
        return prev;
      }
      
      // Current question is not visible, but be VERY conservative:
      // Only navigate if this is initial load OR if current section has no visible questions at all
      // NEVER navigate away from a question the user is currently typing in
      
      // If not initial load and current section has visible questions, don't navigate
      // (let handleNext handle navigation during normal flow)
      if (!isInitialLoad && currentSectionVisibleQs.length > 0) {
        // Current section has visible questions, don't navigate - user might be typing
        // Just ensure index is safe
        const currentQuestionInFullArray = currentSectionObj.questions.findIndex(q => q.id === currentQuestionId);
        if (currentQuestionInFullArray !== -1) {
          // Question exists in full array, keep current position
          return prev;
        }
        // Question not found, adjust to safe index
        const safeIndex = Math.min(prev.currentQuestion, currentSectionVisibleQs.length - 1);
        if (safeIndex !== prev.currentQuestion) {
          return {
            ...prev,
            currentQuestion: safeIndex
          };
        }
        return prev;
      }
      
      // Current section has no visible questions OR this is initial load, find first visible question
      let firstVisibleSectionIndex = -1;
      let firstVisibleQuestionIndex = -1;
      
      // First, check if there are visible questions in the current section
      if (currentSectionVisibleQs.length > 0) {
        // Stay in current section, just move to first visible question
        return {
          ...prev,
          currentQuestion: 0
        };
      }
      
      // Current section has no visible questions, find first visible question in any section
      for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex++) {
        const section = sections[sectionIndex];
        const visibleQs = getVisibleQuestions(section, formData, sectionIndex);
        if (visibleQs.length > 0) {
          firstVisibleSectionIndex = sectionIndex;
          firstVisibleQuestionIndex = 0;
          break;
        }
      }
      
      // If no visible questions found, don't change state
      if (firstVisibleSectionIndex === -1) {
        return prev;
      }
      
      // Only navigate if we're not already at the target
      if (prev.currentSection !== firstVisibleSectionIndex || prev.currentQuestion !== firstVisibleQuestionIndex) {
        return {
          ...prev,
          currentSection: firstVisibleSectionIndex,
          currentQuestion: firstVisibleQuestionIndex
        };
      }
      
      return prev; // No changes needed
    });
  }, [sections, formData]); // Run when sections or formData changes, but logic is conservative

  // Calculate progress and check for completed sections
  useEffect(() => {
    if (sections.length === 0) return;
    
    const totalQuestions = sections.reduce(
      (total, section, sectionIndex) => total + getVisibleQuestions(section, formData, sectionIndex).length,
      0
    );
    const answeredQuestions = state.responses.size;
    const progress = totalQuestions > 0 ? (answeredQuestions / totalQuestions) * 100 : 0;
    
    // Check which sections are completed
    // IMPORTANT: Once a section is marked complete, it stays complete unless required questions become unanswered
    // This prevents sections from appearing incomplete when conditional questions are hidden
    setState(prev => {
      const newCompletedSections = new Set(prev.completedSections);
      
      sections.forEach((section, sectionIndex) => {
        const sectionKey = sectionIndex.toString();
        const wasCompleted = prev.completedSections.has(sectionKey);
        
        const visibleQs = getVisibleQuestions(section, formData, sectionIndex);
        const requiredQs = visibleQs.filter(q => q.required);
        
        // Handle sections with no visible questions
        // If a section has no visible questions, it might be:
        // 1. Conditionally hidden (should be considered "skipped" or "completed")
        // 2. Not applicable based on answers
        // 3. Section has no questions defined (incomplete data - NOT complete)
        // 4. Questions are hidden waiting for dependencies
        if (visibleQs.length === 0) {
          // NEVER mark sections with no questions as "Completed"
          // Sections with no questions are either:
          // - Incomplete data (should show as "No Content" not "Completed")
          // - Hidden due to conditionals (should show as "Upcoming" not "Completed")
          // Only preserve wasCompleted if the section was EXPLICITLY navigated through by user
          if (wasCompleted && section.questions.length > 0) {
            // Section was previously completed AND has questions - keep it completed
            // (This handles sections that are conditionally hidden AFTER being completed)
            newCompletedSections.add(sectionKey);
          }
          // Otherwise: do NOT mark as completed
          return; // Skip to next section
        }
        
        // Check if all required visible questions are answered
        const allRequiredAnswered = requiredQs.length > 0 && requiredQs.every(q => {
          const response = prev.responses.get(q.id);
          const answer = response?.answer ?? formData[q.id];
          return answer !== null && answer !== undefined && answer !== '';
        });
        
        // Check if all currently visible questions are answered
        const allVisibleAnswered = visibleQs.length > 0 && visibleQs.every(q => {
          const response = prev.responses.get(q.id);
          const answer = response?.answer ?? formData[q.id];
          return answer !== null && answer !== undefined && answer !== '';
        });
        
        // Mark as completed if:
        // 1. All required visible questions are answered, OR
        // 2. All visible questions are answered, OR
        // 3. Section was already completed (preserve completion status)
        if (allRequiredAnswered || (visibleQs.length > 0 && allVisibleAnswered)) {
          newCompletedSections.add(sectionKey);
        } else if (wasCompleted) {
          // Section was previously completed, but now has unanswered required questions
          // Only remove completion if there are required questions that are now unanswered
          if (requiredQs.length > 0 && !allRequiredAnswered) {
            // Check if any required question that was previously answered is now unanswered
            const hasUnansweredRequired = requiredQs.some(q => {
              const response = prev.responses.get(q.id);
              const answer = response?.answer ?? formData[q.id];
              return answer === null || answer === undefined || answer === '';
            });
            
            // Only remove completion if a required question became unanswered
            // Don't remove if questions just became hidden (that's expected with conditional logic)
            if (hasUnansweredRequired) {
              newCompletedSections.delete(sectionKey);
            }
            // Otherwise keep it completed (questions were hidden, not unanswered)
          }
        }
      });
      
      return { 
        ...prev, 
        progress,
        completedSections: newCompletedSections
      };
    });
  }, [state.responses, sections, formData]);

  // Auto-save debounced function - using useMemo to create stable debounced function
  const debouncedAutoSave = useMemo(
    () => debounce(() => {
      const progressData = {
        formData,
        state: {
          ...state,
          responses: Array.from(state.responses.entries())
        }
      };
      localStorage.setItem('questionnaire_progress', JSON.stringify(progressData));
    }, 30000),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [] // Empty deps - debounce creates its own closure, we don't want to recreate it
  );

  // Handle answer change
  const handleAnswerChange = useCallback((questionId: string, answer: any) => {
    const newResponse = {
      questionId,
      answer,
      timestamp: new Date(),
      sectionId: currentSection?.id || ''
    };

    setState(prev => ({
      ...prev,
      responses: new Map(prev.responses.set(questionId, newResponse))
    }));

    // Update form data
    setFormData(prev => ({
      ...prev,
      [questionId]: answer
    }));

    // Auto-save to localStorage every 30 seconds or on significant changes
    debouncedAutoSave();
  }, [currentSection, debouncedAutoSave]);

  // Validate current question
  const validateCurrentQuestion = useCallback((): boolean => {
    if (!currentQuestion) return false;
    
    // Get answer from both responses map and formData
    const response = state.responses.get(currentQuestion.id);
    const answer = response?.answer ?? formData[currentQuestion.id];
    
    console.log('validateCurrentQuestion: Question:', currentQuestion.id, 'Required:', currentQuestion.required, 'Answer:', answer);
    
    if (currentQuestion.required) {
      if (answer === null || answer === undefined || answer === '') {
        console.log('validateCurrentQuestion: Required question has no answer');
        return false;
      }
      
      if (Array.isArray(answer) && answer.length === 0) {
        console.log('validateCurrentQuestion: Required question has empty array');
        return false;
      }
    }
    
    // Additional validation rules
    if (currentQuestion.validation && answer !== null && answer !== undefined && answer !== '') {
      // Handle array of validation rules
      if (Array.isArray(currentQuestion.validation)) {
        for (const rule of currentQuestion.validation) {
          if (rule.type === 'required' && !answer) {
            return false;
          }
          // Add more validation logic as needed
        }
      }
      // String validation is handled in EnhancedQuestion component with inline pattern
    }
    
    return true;
  }, [currentQuestion, state.responses, formData]);

  // Handle next question
  const handleNext = useCallback(() => {
    if (!currentQuestion) {
      console.log('handleNext: No current question');
      return;
    }

    setShowValidation(true);

    const isValid = validateCurrentQuestion();
    console.log('handleNext: Validation result:', isValid, 'Question:', currentQuestion.id);
    
    if (!isValid) {
      console.log('handleNext: Validation failed, not proceeding');
      return;
    }

    // Find next visible question
    const next = getNextQuestion(currentQuestion.id, sections, formData);
    
    if (next) {
      // Check if we're moving to a different section
      const isMovingToNewSection = next.sectionIndex !== state.currentSection;
      
      setState(prev => {
        const newCompletedSections = new Set(prev.completedSections);
        
        // If moving to a new section, mark the previous section as completed
        if (isMovingToNewSection) {
          newCompletedSections.add(prev.currentSection.toString());
        }
        
        return {
        ...prev,
        currentSection: next.sectionIndex,
        currentQuestion: next.questionIndex,
          completedSections: newCompletedSections,
        showValidation: false
        };
      });
    } else {
      // Check if there are more questions in current section
      if (state.currentQuestion < visibleQuestions.length - 1) {
        setState(prev => ({
          ...prev,
          currentQuestion: prev.currentQuestion + 1,
          showValidation: false
        }));
      } else {
        // Move to next section
        if (state.currentSection < sections.length - 1) {
          setState(prev => ({
            ...prev,
            currentSection: prev.currentSection + 1,
            currentQuestion: 0,
            completedSections: new Set([...prev.completedSections, prev.currentSection.toString()]),
            showValidation: false
          }));
        } else {
          // Questionnaire complete
          setState(prev => ({
            ...prev,
            isComplete: true,
            completedSections: new Set([...prev.completedSections, prev.currentSection.toString()])
          }));
          onComplete(formData);
        }
      }
    }
  }, [currentQuestion, validateCurrentQuestion, sections, formData, state, visibleQuestions, onComplete]);

  // Handle section navigation (jump to a specific section)
  const handleSectionClick = useCallback((sectionIndex: number) => {
    if (sectionIndex < 0 || sectionIndex >= sections.length) {
      return;
    }

    const targetSection = sections[sectionIndex];
    if (!targetSection) {
      return;
    }

    // Get visible questions for the target section
    const visibleQuestions = getVisibleQuestions(targetSection, formData, sectionIndex);
    
    if (visibleQuestions.length > 0) {
      // Navigate to the first visible question in the target section
      const firstVisibleQuestion = visibleQuestions[0];
      const questionIndex = targetSection.questions.findIndex(q => q.id === firstVisibleQuestion.id);
      
      if (questionIndex !== -1) {
        setState(prev => ({
          ...prev,
          currentSection: sectionIndex,
          currentQuestion: questionIndex,
          showValidation: false
        }));
      }
    }
  }, [sections, formData]);

  // Handle previous question
  // Uses getPreviousQuestion to find the previous VISIBLE question,
  // ensuring navigation works correctly with conditional questions
  const handlePrevious = useCallback(() => {
    if (!currentQuestion) {
      console.log('handlePrevious: No current question');
      return;
    }

    // Find previous visible question using the same logic as getNextQuestion
    const previous = getPreviousQuestion(currentQuestion.id, sections, formData);
    
    if (previous) {
      // Found a previous visible question, navigate to it
      setState(prev => ({
        ...prev,
        currentSection: previous.sectionIndex,
        currentQuestion: previous.questionIndex,
        showValidation: false
      }));
    } else {
      // No previous visible question found, try to go to last question in previous section
      if (state.currentSection > 0) {
      const prevSection = sections[state.currentSection - 1];
        const prevVisibleQuestions = getVisibleQuestions(prevSection, formData, state.currentSection - 1);
        
        if (prevVisibleQuestions.length > 0) {
          // Find the index of the last visible question in the previous section
          const lastVisibleQuestion = prevVisibleQuestions[prevVisibleQuestions.length - 1];
          const lastQuestionIndex = prevSection.questions.findIndex(q => q.id === lastVisibleQuestion.id);
      
      setState(prev => ({
        ...prev,
        currentSection: prev.currentSection - 1,
            currentQuestion: lastQuestionIndex >= 0 ? lastQuestionIndex : 0,
        showValidation: false
      }));
    }
      }
    }
  }, [currentQuestion, sections, formData, state.currentSection]);

  // Handle save to backend using React Query hook
  const handleSave = useCallback(async () => {
    setSaveError(null);
    
    try {
      // Save to localStorage
      const progressData = {
        formData,
        state: {
          ...state,
          responses: Array.from(state.responses.entries())
        }
      };
      localStorage.setItem('questionnaire_progress', JSON.stringify(progressData));
      
      // Save to backend using React Query mutation
      try {
        await saveQuestionnaire({
          answers: Object.fromEntries(state.responses),
          formData,
          progress: state.progress
        });
      } catch (apiError) {
        console.warn('Could not save to backend, saved locally only:', apiError);
        setSaveError('Saved locally, but could not sync with server');
      }
      
      onSave(formData);
    } catch (error) {
      setSaveError('Failed to save progress. Please try again.');
      console.error('Save error:', error);
    }
  }, [formData, state, saveQuestionnaire, onSave]);

  // Get current answer value
  const getCurrentAnswer = useCallback(() => {
    if (!currentQuestion) return null;
    const response = state.responses.get(currentQuestion.id);
    return response?.answer ?? formData[currentQuestion.id] ?? null;
  }, [currentQuestion, state.responses, formData]);

  // Show country/year selection screen first
  if (showSelection) {
    return (
      <CountryYearSelection
        onSelect={handleCountryYearSelect}
        initialCountry={selectedCountry || undefined}
        initialYear={selectedYear || undefined}
      />
    );
  }

  // Loading state
  if (loading || isLoadingQuestionnaire) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center py-12">
          <Loader className="w-12 h-12 text-primary-600 animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Loading Questionnaire...
          </h2>
          <p className="text-gray-600">
            Please wait while we load your {selectedCountry} {selectedYear} tax questions.
          </p>
        </div>
      </div>
    );
  }

  // Complete state
  if (state.isComplete) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-green-900 mb-2">
            Questionnaire Complete!
          </h2>
          <p className="text-green-700 mb-6">
            Thank you for completing the tax questionnaire. Your information has been saved.
          </p>
          <div className="space-x-4">
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
            >
              Start New Questionnaire
            </button>
            <button
              onClick={handleSave}
              className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
            >
              Save & Continue Later
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!currentQuestion || !currentSection) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            No Questions Available
          </h2>
          <p className="text-gray-600">Please check your answers to see available questions.</p>
        </div>
      </div>
    );
  }

  const sectionTitles = sections.map(section => section.title);
  const isFirst = state.currentSection === 0 && state.currentQuestion === 0;
  const isLast = state.currentSection === sections.length - 1 && 
                 state.currentQuestion === visibleQuestions.length - 1;

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-3xl font-bold text-gray-900">
            {selectedCountry === 'USA' ? 'United States' : 'Spain'} Tax Filing Questionnaire
          </h1>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setShowClearConfirm(true)}
              className="text-sm text-red-600 hover:text-red-700 underline flex items-center space-x-1"
              title="Clear all questionnaire data"
            >
              <Trash2 className="w-4 h-4" />
              <span>Clear</span>
            </button>
            <button
              onClick={() => setShowSelection(true)}
              className="text-sm text-primary-600 hover:text-primary-700 underline"
            >
              Change Country/Year
            </button>
          </div>
        </div>
        <p className="text-gray-600">
          Tax Year: {selectedYear} | Please answer all questions to help us prepare your tax return accurately.
          Your progress is automatically saved.
        </p>
      </div>

      {/* Clear Confirmation Modal */}
      {showClearConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <Trash2 className="w-6 h-6 text-red-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Clear Questionnaire Data?
                  </h3>
                </div>
                <button
                  onClick={() => setShowClearConfirm(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <p className="text-gray-600 mb-6">
                Are you sure you want to clear all questionnaire data? This will remove:
              </p>
              
              <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
                <li>All your answers</li>
                <li>Saved progress</li>
                <li>Current session data</li>
              </ul>
              
              <p className="text-red-600 font-medium mb-6">
                This action cannot be undone. You will need to start over.
              </p>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowClearConfirm(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    // Clear all questionnaire-related data
                    localStorage.removeItem('questionnaireData');
                    localStorage.removeItem('questionnaire_progress');
                    
                    // Clear any other related keys
                    const keysToRemove: string[] = [];
                    for (let i = 0; i < localStorage.length; i++) {
                      const key = localStorage.key(i);
                      if (key && (key.startsWith('questionnaire') || key.startsWith('tax_calc'))) {
                        keysToRemove.push(key);
                      }
                    }
                    keysToRemove.forEach(key => localStorage.removeItem(key));
                    
                    // Reset state
                    setState({
                      currentSection: 0,
                      currentQuestion: 0,
                      responses: new Map(),
                      completedSections: new Set(),
                      progress: 0,
                      isComplete: false
                    });
                    setFormData({});
                    setShowClearConfirm(false);
                    
                    // Reload to start fresh
                    window.location.reload();
                  }}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  Yes, Clear All Data
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Progress Sidebar - scrollable when many sections */}
        <div className="lg:col-span-1 max-h-[calc(100vh-6rem)] overflow-y-auto lg:sticky lg:top-6">
          <ProgressBar
            currentSection={state.currentSection + 1}
            totalSections={sections.length}
            completedSections={state.completedSections}
            sectionTitles={sectionTitles}
            progress={state.progress}
            onSectionClick={handleSectionClick}
          />
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">
          {/* Section Header */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {currentSection.title}
            </h2>
            {currentSection.description && (
              <p className="text-gray-600">{currentSection.description}</p>
            )}
            <div className="mt-2 text-sm text-gray-500">
              Question {currentQuestionIndexInVisible + 1} of {visibleQuestions.length} in this section
            </div>
          </div>

          {/* Question */}
          <EnhancedQuestion
            question={currentQuestion}
            value={getCurrentAnswer()}
            onChange={(value) => handleAnswerChange(currentQuestion.id, value)}
            onNext={handleNext}
            onPrevious={handlePrevious}
            isFirst={isFirst}
            isLast={isLast}
            showValidation={showValidation}
            formData={formData}
            allResponses={state.responses}
          />

          {/* Save Error */}
          {saveError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              {saveError}
            </div>
          )}

          {/* Action Buttons */}
          <div className="mt-6 flex justify-between items-center">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center space-x-2 px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {isSaving ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              <span>{isSaving ? 'Saving...' : 'Save Progress'}</span>
            </button>

            <div className="text-sm text-gray-500">
              {Math.round(state.progress)}% complete
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Debounce utility function
function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export default EnhancedQuestionnaire;
